/**
 * Shared Recharts `<Line>` props for the "dashed bridge across gaps".
 *
 * Average/rate metrics keep empty days as `null`, so the solid
 * `connectNulls={false}` line breaks across an interior gap and two data runs
 * read as disconnected. Rendering this second line *underneath* the solid one
 * (declared first) with `connectNulls` and a dashed stroke draws a connector
 * only where the solid line is absent — i.e. across the gaps. The dashed style
 * signals "no data here, just bridging" rather than fabricating values.
 *
 * Spread onto a literal `<Line>` (Recharts only recognises literal children):
 *   <Line dataKey="value" stroke={CHART_COLOR} {...GAP_BRIDGE_LINE_PROPS} />
 *
 * `type: 'linear'` is load-bearing, not cosmetic. The bridge must trace the
 * exact same path as the solid line on the data runs (so it stays hidden
 * underneath) and add segments only across the gaps. A straight segment between
 * two points is neighbour-independent, so the bridge and the solid line
 * coincide there. `monotone` computes each point's tangent from its neighbours,
 * and `connectNulls` gives the bridge a different neighbour set than the broken
 * solid line, so the two curves diverge and the dashed line bows out alongside
 * the solid one on days that have data. The solid line it sits under must also
 * be `linear` for the same reason.
 */

// A no-op dot: Recharts forces `renderDots` for a single-point series even when
// `dot={false}`, which would paint a duplicate marker on top of the solid
// line's isolated dot. Returning an empty group keeps the bridge dot-less.
const renderNoDot = ({ key }: { key?: string }) => <g key={key} />;

export const GAP_BRIDGE_LINE_PROPS = {
  type: 'linear',
  strokeWidth: 2,
  strokeDasharray: '4 4',
  connectNulls: true,
  dot: renderNoDot,
  activeDot: false,
  legendType: 'none',
} as const;
